import { createContext, useContext, useMemo, type ReactNode } from "react";

import type { PublicDirectory, TrainerPageConfig } from "../types";

/**
 * The facts a public page prints that are not in the calendar: phone, address, opening hours as
 * prose, and the numbers on the home page.
 *
 * They belong to a trainer, so they arrive with the page at /<slug> and ride in a context.
 * Outside a provider every fact is empty: a page with no trainer behind it has nobody's phone to
 * print, and an empty fact is one the sections leave out rather than one they make up.
 */
export type SiteConfig = {
  /**
   * The trainer whose page this is, or null on the platform's own pages. Every link in the site
   * chrome is built from it, so one set of sections serves every trainer.
   */
  slug: string | null;
  /**
   * The business behind the page, which a booking made from it has to name. Null on the
   * platform's own pages, where there is no one trainer to book.
   */
  organizationId: string | null;
  phone: string;
  /** The same number in the form a `tel:` link wants. */
  phoneHref: string;
  email: string;
  address: string;
  mapUrl: string;
  hours: { weekdays: string; saturday: string | null; sunday: string | null };
  /** The trainer's own numbers. Each one is optional: an empty one is not shown. */
  stats: { sessionsCoached: string; since: string; rebookRate: string };
  calendarDays: number;
  /**
   * The services, coaches and open times read with the page, so the server's HTML holds them and
   * the page arrives filled. Null on the platform's own pages and when the server could not ask.
   */
  directory: PublicDirectory | null;
  /**
   * Whose page this is, as the visitor should see it at once: the business's name and logo in the
   * bar, and on the first card the trainer's photo and their own words. Null on the platform's
   * own pages, which wear the platform's brand.
   */
  brand: {
    name: string;
    logoUrl: string | null;
    coverUrl: string | null;
    headline: string;
    lede: string;
  } | null;
};

const FALLBACK: SiteConfig = {
  slug: null,
  organizationId: null,
  phone: "",
  phoneHref: "",
  email: "",
  address: "",
  mapUrl: "",
  hours: { weekdays: "", saturday: null, sunday: null },
  stats: { sessionsCoached: "", since: "", rebookRate: "" },
  calendarDays: 5,
  directory: null,
  brand: null,
};

const SiteConfigContext = createContext<SiteConfig>(FALLBACK);

/** `tel:` wants the number with nothing but digits and a leading plus. */
const telHref = (phone: string) => `tel:${phone.replaceAll(/[^+\d]/g, "")}`;

/** Falls back to an OpenStreetMap search when the trainer has not pasted their own map link. */
const mapLink = ({ mapUrl, address }: { mapUrl: string; address: string }) =>
  mapUrl || (address ? `https://www.openstreetmap.org/search?query=${encodeURIComponent(address)}` : "");

/** The business behind a trainer's page, as its public page names it. */
export type SiteBusiness = { name: string; logoUrl: string | null };

/** Turns a trainer's stored page into the shape the template's sections read. */
export const toSiteConfig = (
  page: TrainerPageConfig,
  business: SiteBusiness,
  directory: PublicDirectory | null = null,
): SiteConfig => ({
  slug: page.slug,
  organizationId: page.organizationId,
  phone: page.phone,
  phoneHref: page.phone ? telHref(page.phone) : "",
  email: page.email,
  address: page.address,
  mapUrl: mapLink(page),
  hours: page.hours,
  stats: {
    sessionsCoached: page.stats.sessionsCoached,
    since: page.stats.since,
    rebookRate: page.stats.rebookRate,
  },
  calendarDays: page.calendarDays,
  directory,
  brand: {
    name: business.name,
    logoUrl: business.logoUrl,
    coverUrl: page.coverUrl,
    headline: page.headline,
    lede: page.lede,
  },
});

export function SiteConfigProvider({
  page,
  business,
  directory = null,
  children,
}: {
  page: TrainerPageConfig | null;
  business: SiteBusiness | null;
  directory?: PublicDirectory | null;
  children: ReactNode;
}) {
  const value = useMemo(
    () => (page && business ? toSiteConfig(page, business, directory) : FALLBACK),
    [page, business, directory],
  );
  return <SiteConfigContext.Provider value={value}>{children}</SiteConfigContext.Provider>;
}

export const useSiteConfig = () => useContext(SiteConfigContext);

/**
 * The one way to reach the trainer that the page can offer when there is nothing to book: the
 * phone, else the email, else nothing at all.
 */
export const contactOf = (site: SiteConfig) =>
  site.phone
    ? { kind: "phone" as const, href: site.phoneHref, label: site.phone }
    : site.email
      ? { kind: "email" as const, href: `mailto:${site.email}`, label: site.email }
      : null;

/**
 * Where a link in the site chrome points. Inside a trainer's page every page of the template
 * lives under their slug; with no slug (the platform's own pages) the paths are the platform's.
 */
export const sitePath = (slug: string | null, page: "" | "services" | "coaches" | "pricing" | "contact" | "shop" = "") => {
  const suffix = page ? `/${page}` : "";
  return slug ? `/${slug}${suffix}` : `/${page}`;
};

/** One shop product's page under whichever trainer's site we are on. */
export const productPath = (slug: string | null, productId: string) =>
  slug ? `/${slug}/shop/${productId}` : `/shop/${productId}`;

/** One booking service's page under whichever trainer's site we are on. */
export const servicePath = (slug: string | null, serviceId: string) =>
  slug ? `/${slug}/services/${serviceId}` : `/services/${serviceId}`;
